import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import clsx from "clsx";
import { Children, FC, useEffect, useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  get,
  Path,
  useFormState,
  useWatch,
} from "react-hook-form";
import Select, { Props, components } from "react-select";
import colors from "tailwindcss/colors";

interface StyledSelectProps<T extends FieldValues> extends Props {
  title?: string;
  control: Control<T>;
  name: Path<T>;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  setValue: (e: any) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}

const { ValueContainer, Placeholder } = components;

const CustomValueContainer: FC<any> = ({ children, ...props }) => {
  return (
    <ValueContainer {...props}>
      <Placeholder {...props} isFocused={props.isFocused}>
        {props.selectProps.placeholder}
      </Placeholder>
      {Children.map(children, (child) => (child && child.type !== Placeholder ? child : null))}
    </ValueContainer>
  );
};

export const StyledSelectSingleValue: FC<StyledSelectProps<any>> = ({
  title,
  control,
  name,
  className,
  setValue,
  options,
  disabled,
  ...props
}) => {
  const [selected, setSelected] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const { errors } = useFormState({ control });
  const translateMessage = useTranslateMessage();
  const errorMessage = get(errors, name + ".message") as unknown as string;

  const watch = useWatch({
    control,
    name,
  });

  useEffect(() => {
    const macthingOption: { value: string; label: string } | null =
      options.find((el: { value: string; label: string }) => el.value === watch) || null;
    if (macthingOption) {
      setSelected(macthingOption);
      setValue(macthingOption?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const macthingOption: { value: string; label: string } | null =
      options.find((el: { value: string; label: string }) => el.value === watch) || null;
    if (macthingOption) {
      setSelected(macthingOption);
      setValue(macthingOption?.value);
    } else {
      setSelected(null);
      setValue(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const handleChange = (e: any) => {
    const macthingOption: { value: string; label: string } | null =
      options.find((el: { value: string; label: string }) => el.value === e) || null;
    if (macthingOption) {
      setSelected(macthingOption);
      setValue(macthingOption?.value);
    }
  };

  return (
    <div className={clsx("flex flex-col", className)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            {...field}
            {...props}
            placeholder={title}
            options={options}
            onChange={(e: any) => handleChange(e.value)}
            value={selected}
            isDisabled={disabled}
            components={{
              IndicatorSeparator: () => null,
              ValueContainer: CustomValueContainer,
            }}
            className={clsx([className])}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderColor: colors.gray[200],
                height: "3.5rem",
                borderRadius: "0.5rem",
              }),
              placeholder: (baseStyles) => ({
                ...baseStyles,
                transform: watch ? "translateY(-1rem)" : "translateY(0)",
                fontSize: ".9rem",
                color: colors.gray[500],
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
                overflow: "visible",
                marginLeft: "0.4rem",
              }),
              singleValue: (baseStyles, state) => ({
                ...baseStyles,
                transform: state.hasValue ? "translateY(0.3rem)" : "translateY(0)",
              }),
            }}
          />
        )}
      />
      {errorMessage !== "" ? (
        <p className="text-xs text-red-500">{translateMessage(errorMessage)}</p>
      ) : null}
    </div>
  );
};
